import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, Award, Target, CheckCircle } from "lucide-react";

const openPositions = [
  { title: "Senior Lead Auditor — ISO 27001", location: "Remote / Global", type: "Full-time", desc: "We're looking for experienced ISO 27001 Lead Auditors to join our growing team. Minimum 5 years of audit experience required." },
  { title: "Cybersecurity Consultant", location: "Remote", type: "Full-time", desc: "Join our cybersecurity assurance team to conduct penetration testing, vulnerability assessments, and security architecture reviews." },
  { title: "Compliance Analyst", location: "Remote", type: "Full-time", desc: "Support certification engagements by conducting gap analyses, document reviews, and compliance assessments." },
  { title: "Training Program Manager", location: "Remote", type: "Full-time", desc: "Design and manage our professional training programs, including lead auditor courses and security awareness workshops." },
  { title: "Business Development Manager", location: "Remote / Global", type: "Full-time", desc: "Drive growth by identifying and securing new certification and assurance engagements across key markets." },
];

const values = [
  { icon: Target, title: "Integrity", desc: "We maintain independence and impartiality in all our assessments." },
  { icon: Award, title: "Excellence", desc: "We deliver the highest quality certification and assurance services." },
  { icon: Users, title: "Collaboration", desc: "We work as a team with our clients, partners, and colleagues." },
  { icon: CheckCircle, title: "Accountability", desc: "We take ownership of our work and its impact on our clients." },
];

function HeroSection() {
  return (
    <section className="bg-[#0B2545] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">Careers</p>
        <h1 data-testid="careers-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
          Join Our Team
        </h1>
        <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
          Be part of a team that's shaping the future of certification and cybersecurity assurance. We're looking for talented professionals who share our commitment to excellence.
        </p>
      </div>
    </section>
  );
}

function ValuesBar() {
  return (
    <section className="py-16 bg-[#F5F7FA] border-b border-[#0B2545]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#0B2545]/10 border border-[#0B2545]/10">
          {values.map((v) => (
            <div key={v.title} className="p-6 lg:p-8 bg-white text-center">
              <v.icon size={24} className="mx-auto text-[#13A8BD] mb-3" strokeWidth={1.5} />
              <h3 className="text-sm font-bold text-[#0B2545] mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>{v.title}</h3>
              <p className="text-xs text-slate-500">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PositionsSection() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">Open Positions</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>Current Openings</h2>
        </div>
        <div className="space-y-0 border border-[#0B2545]/10">
          {openPositions.map((pos, i) => (
            <div key={pos.title} data-testid={`career-position-${i}`} className="p-6 lg:p-8 border-b border-[#0B2545]/10 last:border-b-0 hover:bg-[#F5F7FA] transition-colors group">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-[#0B2545] group-hover:text-[#13A8BD] transition-colors" style={{ fontFamily: "Chivo, sans-serif" }}>{pos.title}</h3>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} className="text-[#13A8BD]" /> {pos.location}</span>
                    <span className="text-xs px-2 py-0.5 border border-[#0B2545]/15 text-slate-500">{pos.type}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{pos.desc}</p>
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-[#13A8BD] uppercase tracking-wider hover:text-[#0e8a9c] transition-colors flex-shrink-0">
                  Apply <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="py-16 bg-[#0B2545]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
        <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Don't See a Fit?</h2>
        <p className="text-sm text-white/60 mb-6 max-w-md mx-auto">We're always looking for exceptional talent. Send us your resume and we'll keep you in mind for future opportunities.</p>
        <Link to="/contact" data-testid="careers-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
          Send Your Resume <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

export default function CareersPage() {
  return (
    <div className="page-transition pt-20" data-testid="careers-page">
      <HeroSection />
      <ValuesBar />
      <PositionsSection />
      <CtaSection />
    </div>
  );
}
